import { formatAbn } from "utils/abn";
import React, { useEffect, useRef, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { searchSignupAbrCompanies, verifySignupAbrAbn } from "utils/abrAPI";
import "./style.css";

export default function SignupAbrLookup({
  companyName,
  abn,
  organizationType,
  onApply,
}) {
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState([]);
  const [message, setMessage] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setBusy(false);
    setMatches([]);
    setMessage("");
    return () => {
      requestId.current += 1;
    };
  }, [companyName, abn, organizationType]);

  const lookup = async (selectedAbn) => {
    const request = ++requestId.current;
    setBusy(true);
    setMessage("");
    setMatches([]);
    try {
      const result = selectedAbn
        ? [{ ...(await verifySignupAbrAbn(selectedAbn)), verified: true }]
        : (await searchSignupAbrCompanies(companyName)).matches;
      if (request !== requestId.current) return;
      setMatches(result);
      if (!result.length) setMessage("No matching businesses found.");
    } catch (error) {
      if (request === requestId.current) setMessage(error.message);
    } finally {
      if (request === requestId.current) setBusy(false);
    }
  };

  return (
    <section className="signup-abr" aria-label="ABR business lookup">
      <div className="signup-abr-heading">
        <strong>Find your registered details</strong>
        <p>
          Search by legal name or check your ABN, then choose the details to
          use.
        </p>
      </div>
      <div className="signup-abr-actions">
        <button
          type="button"
          disabled={busy || companyName.trim().length < 2}
          onClick={() => lookup()}
        >
          <LuSearch aria-hidden="true" /> Find business
        </button>
        <button
          type="button"
          disabled={busy || !abn.trim()}
          onClick={() => lookup(abn)}
        >
          Check registration
        </button>
        <a
          href="https://abr.business.gov.au/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Search ABN Lookup
        </a>
      </div>
      <div aria-live="polite" aria-busy={busy}>
        {busy && <p className="signup-abr-message">Looking up registration…</p>}
        {message && <p className="signup-abr-message">{message}</p>}
        {matches.length > 0 && (
          <ul className="signup-abr-results">
            {matches.map((business, index) => (
              <li key={`${business.abn}-${index}`}>
                <div className="signup-abr-details">
                  <strong>{business.companyName}</strong>
                  <span>
                    ABN {formatAbn(business.abn)}
                    {business.state ? ` · ${business.state}` : ""}
                  </span>
                  {business.acn && <span>ACN {business.acn}</span>}
                  {business.verified && (
                    <span
                      className={`signup-abr-status ${
                        business.active ? "is-active" : ""
                      }`}
                    >
                      {business.status} registration
                    </span>
                  )}
                  {business.verified &&
                    business.active &&
                    organizationType === "Business" &&
                    !business.acn && (
                      <span>
                        No ACN returned. Enter your company ACN above.
                      </span>
                    )}
                </div>
                <button
                  type="button"
                  disabled={busy || (business.verified && !business.active)}
                  onClick={() => {
                    if (!business.verified) return lookup(business.abn);
                    onApply(business);
                    setMatches([]);
                  }}
                >
                  {business.verified ? "Use details" : "Check registration"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
